import { ReactNode, useRef, useState, useEffect } from 'react';
import { Text, makeStyles, mergeClasses } from '@fluentui/react-components';
import { Add16Regular, Subtract16Regular } from '@fluentui/react-icons';

const useStyles = makeStyles({
  accordionButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: '16px 0',
    marginTop: '16px',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: '#50BCBD',
    transition: 'opacity 0.2s ease-in-out',
    '&:hover': {
      opacity: 0.8,
    },
  },
  contentWrapper: {
    overflow: 'hidden',
    transition: 'max-height 0.3s ease-in-out, opacity 0.3s ease-in-out',
  },
  contentInner: {
    paddingTop: '8px',
  },
  icon: {
    minWidth: '16px',
  },
});

type AccordionProps = {
  title: string;
  activeTitle?: string;
  titleClassName?: string;
  className?: string;
  children: ReactNode;
  defaultOpen?: boolean;
};

export const Accordion = ({
  title,
  titleClassName,
  className,
  children,
  activeTitle,
  defaultOpen = false,
}: AccordionProps) => {
  const styles = useStyles();
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [maxHeight, setMaxHeight] = useState<string>(defaultOpen ? 'none' : '0px');
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && contentRef.current) {
      setMaxHeight(`${contentRef.current.scrollHeight}px`);
    } else {
      setMaxHeight('0px');
    }
  }, [isOpen, children]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div>
      <button
        className={mergeClasses(styles.accordionButton, className)}
        onClick={handleToggle}
        aria-expanded={isOpen}
      >
        <Text size={300} weight="semibold" className={titleClassName}>
          {isOpen ? activeTitle : title}
        </Text>
        {isOpen ? (
          <Subtract16Regular className={styles.icon} aria-hidden="true" />
        ) : (
          <Add16Regular className={styles.icon} aria-hidden="true" />
        )}
      </button>
      <div
        className={styles.contentWrapper}
        style={{
          maxHeight,
          opacity: isOpen ? 1 : 0,
        }}
      >
        <div ref={contentRef} className={styles.contentInner}>
          {children}
        </div>
      </div>
    </div>
  );
};
